/**
 * DrawnOutWarningCard
 *
 * Dashboard reminder shown to admin/delegate users when one or more
 * events have been finalized but still have drawn-out / unplaced
 * registrations that haven't been cancelled (and therefore haven't
 * been refunded). Mirrors the per-event Cancel Drawn-Out button on
 * the draw page — both share the backend's drawn-out-status check
 * as the single source of truth so they never disagree about whether
 * action is needed.
 *
 * Why this exists: publish/finalize no longer auto-cancels drawn-out
 * contestants — many orgs grant a 24-hour call-in window after the
 * draw posts. The warning makes sure admins don't forget to run the
 * cleanup after that window closes.
 *
 * Accessibility: the attention-grabbing pulse only animates when the
 * user has NOT requested reduced motion (respects prefers-reduced-
 * motion via Tailwind's motion-safe variant).
 */

import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useFinalizedWithDrawnOut } from '@/api/extensions/useDrawOps';

export function DrawnOutWarningCard() {
  const navigate = useNavigate();
  const { data, isLoading } = useFinalizedWithDrawnOut();
  const events = data?.events ?? [];

  // Hide the card entirely when there's nothing to act on — admins
  // don't need to see "0 drawn-out entries pending" on their
  // dashboard every day. The check runs cheaply on mount and stays
  // out of the way until it's relevant.
  if (isLoading) return null;
  if (events.length === 0) return null;

  const totalCount = events.reduce(
    (sum, ev) => sum + (ev.drawn_out_count ?? 0),
    0,
  );

  const goToDraw = (
    eventId: string,
    eventName: string,
    rodeoId: string | null,
  ) => {
    navigate('/delegate/draws', {
      state: {
        eventId,
        eventName,
        rodeoId,
        drawKind: 'rodeo',
      },
    });
  };

  return (
    <Card
      className="border-amber-300 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30"
      role="alert"
      aria-live="polite"
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-200/80 dark:bg-amber-900/50 p-2 motion-safe:animate-pulse">
            <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-amber-900 dark:text-amber-100">
              Drawn-out entries waiting to be cancelled
            </h3>
            <p className="text-sm text-amber-800/90 dark:text-amber-200/90 mt-0.5 leading-relaxed">
              <strong>{events.length}</strong>{' '}
              {events.length === 1 ? 'event has' : 'events have'} been
              finalized with{' '}
              <strong>
                {totalCount} drawn-out{' '}
                {totalCount === 1 ? 'entry' : 'entries'}
              </strong>{' '}
              that haven't been cancelled yet. Cancelling will refund
              affected contestants (minus the transaction fee).
            </p>
          </div>
        </div>

        <div className="space-y-2 pl-1">
          {events.map((ev) => (
            <div
              key={ev.event_id}
              className="flex items-center justify-between gap-3 rounded-md border border-amber-200/70 dark:border-amber-900/40 bg-white/60 dark:bg-amber-950/20 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{ev.event_name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {ev.rodeo_name}
                  {ev.finalized_at && (
                    <>
                      {' · finalized '}
                      {new Date(ev.finalized_at).toLocaleDateString()}
                    </>
                  )}
                </p>
              </div>
              <Badge
                variant="outline"
                className="border-amber-400/60 bg-amber-100/70 text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/40 dark:text-amber-100 shrink-0"
              >
                {ev.drawn_out_count} drawn-out
              </Badge>
              <Button
                size="sm"
                variant="default"
                className="shrink-0 bg-amber-700 hover:bg-amber-800 text-white"
                onClick={() => goToDraw(ev.event_id, ev.event_name, ev.rodeo_id)}
              >
                Review & cancel
                <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

